import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { CloseIcon } from "@/assets/icons";
import RadioButton from "@/components/ui/radio";
import { MESSAGE } from "@/constants/message";
import { cn } from "@/lib/utils";
import { RootState } from "@/store";
import { saveWeightValues } from "@/store/slice/datasetSlice";
import { setSpatialConfig } from "@/store/slice/spatialSlice";
import { type WeightValues } from "@/types/dataset";
import { ScoringOptions, ScoringOptionsEnum } from "@/types/spatial";
import { areObjectsDifferentDeeply } from "@/utils/common";
import { getAvailableDatasets } from "@/utils/dataset";

import { Button } from "equator-design-system";
import { twMerge } from "tailwind-merge";

import DatasetAttributeItem from "./DatasetAttributeItem";

const TEST_ID_PREFIX = "data-weight-settings-popup";

interface Props {
  onClose: () => void;
}

const DataWeightSettingsPopup = ({ onClose }: Props) => {
  const dispatch = useDispatch();
  const { datasets, weightValues, visualizationOrder } = useSelector(
    (state: RootState) => state.dataset
  );

  const defaultWeightValues: WeightValues = useMemo(() => {
    const weightCount = Object.values(weightValues).reduce(
      (sum, datasetWeights) =>
        sum + Object.keys(datasetWeights.attributes || {}).length,
      0
    );
    const defaultWeight = (100 / weightCount).toFixed(2).toString();

    // Create new weight values object with default weights
    const resetWeights: WeightValues = {};

    Object.entries(weightValues).forEach(([datasetId, categoryWeights]) => {
      resetWeights[datasetId] = { weight: "", attributes: {} };

      // Set each attribute to default weight
      Object.keys(categoryWeights.attributes).forEach((categoryId) => {
        resetWeights[datasetId].attributes[categoryId] = {
          weight: defaultWeight
        };
      });
    });
    return resetWeights;
  }, [weightValues]);

  const availableDatasets = getAvailableDatasets(datasets);
  const { spatialConfig } = useSelector((state: RootState) => state.spatial);
  const { scoringOption: scoringOptionState } = spatialConfig;

  const [localWeightValues, setLocalWeightValues] =
    useState<WeightValues>(weightValues);
  const [selectedScoringOption, setSelectedScoringOption] =
    useState<ScoringOptions | null>(
      scoringOptionState || ScoringOptionsEnum.RAW
    );

  // Effect to adjust weight distribution when there is a new dataset added
  useEffect(() => {
    // Check if localWeightValues and weightValues have the same dataset IDs
    const localDatasetIds = Object.keys(localWeightValues);
    const weightValuesDatasetIds = Object.keys(weightValues);

    const hasSameDatasetIds =
      localDatasetIds.length === weightValuesDatasetIds.length &&
      localDatasetIds.every((id) => weightValuesDatasetIds.includes(id));

    if (!hasSameDatasetIds) {
      setLocalWeightValues(weightValues);
    }
  }, [localWeightValues, weightValues]);

  const onSaveSetting = useCallback(() => {
    // Validate all weight values before saving
    const isValid = Object.entries(localWeightValues).every(([_, dataset]) => {
      return Object.entries(dataset.attributes).every(([_, attribute]) => {
        return (
          attribute.weight === "" ||
          /^[0-9,]+(\.[0-9]*)?$/.test(attribute.weight)
        );
      });
    });

    if (!isValid) {
      // If any weight value is invalid, don't save
      console.error("Invalid weight values detected");
      return;
    }

    // Save all weight values at once
    dispatch(saveWeightValues({ weightValues: localWeightValues }));
    // Save selected scoring option

    dispatch(
      setSpatialConfig({
        ...spatialConfig,
        scoringOption: selectedScoringOption
      })
    );
    onClose();
  }, [
    dispatch,
    localWeightValues,
    onClose,
    spatialConfig,
    selectedScoringOption
  ]);

  const resetAllWeights = () => {
    setLocalWeightValues(defaultWeightValues);
  };

  const handleUpdateWeightValue = (
    datasetId: string,
    categoryIdx: string,
    value: string
  ) => {
    // Always update with the new value, let the input component handle validation
    const newWeightValues: WeightValues = {
      ...localWeightValues,
      [datasetId]: {
        ...(localWeightValues[datasetId] || {}),
        attributes: {
          ...localWeightValues[datasetId].attributes,
          [categoryIdx]: {
            weight: value
          }
        }
      }
    };
    setLocalWeightValues(newWeightValues);
  };

  const totalWeight = (): number => {
    let total = 0;

    Object.entries(localWeightValues).forEach(([_, dataset]) => {
      Object.entries(dataset.attributes).forEach(([_, attribute]) => {
        // Remove commas before parsing
        const cleanWeightStr = attribute?.weight?.replace(/,/g, "");
        const weight = parseFloat(cleanWeightStr);
        if (!isNaN(weight)) {
          total += weight;
        }
      });
    });
    return total;
  };

  const totalAttributes = Object.values(weightValues).reduce(
    (sum, dataset) => sum + Object.keys(dataset.attributes || {}).length,
    0
  );

  const hasWeightValuesChanged = areObjectsDifferentDeeply(
    localWeightValues,
    defaultWeightValues
  );

  const checkIfAllWeightsAreZero = (
    weightsByIndicator: WeightValues
  ): boolean => {
    return !Object.values(weightsByIndicator).some((datasetWeights) =>
      Object.values(datasetWeights.attributes || {}).some((attribute) => {
        const weightValue = attribute?.weight ?? "";
        return weightValue !== "0" && weightValue !== "";
      })
    );
  };

  const isAllWeightZeroResult = checkIfAllWeightsAreZero(localWeightValues);

  const totalPercentage = totalWeight() > 0 ? "100%" : "0%";

  return (
    <div
      className={twMerge(
        "z-[99] font-sans border-gainsboro bg-white rounded-lg w-[338px] max-h-[90vh]",
        "flex flex-col pt-4 absolute left-[370px] top-[30px] text-xs"
      )}
    >
      {/* Header */}
      <div className="flex flex-row justify-between items-start px-4">
        <div className="flex flex-row items-center gap-2">
          <h2 className="text-txtHeading font-bold text-[20px]">
            Setting Data Weight
          </h2>
        </div>
        <button
          className="bg-transparent border-0 p-0"
          onClick={onClose}
          type="button"
          aria-label="Close"
        >
          <CloseIcon className="fill-dim-gray" />
        </button>
      </div>
      {/* Dataset Attributes Weight Section */}
      <div className="flex flex-col mt-3 custom-scrollbar overflow-y-auto divide-y-8 gap-2 divide-white-smoke scrollbar-overlay">
        {visualizationOrder.map((datasetId, index) => (
          <DatasetAttributeItem
            key={datasetId}
            datasetId={datasetId}
            dataset={availableDatasets[datasetId]}
            isFirst={index === 0}
            localWeightValues={localWeightValues}
            isAllWeightInvalid={isAllWeightZeroResult}
            totalWeightValue={totalWeight()}
            onUpdateWeight={handleUpdateWeightValue}
          />
        ))}
        {/* Scoring Option Section */}
        <div
          className={cn(
            "flex flex-col px-4 gap-1 pb-6",
            visualizationOrder.length ? "pt-6" : ""
          )}
        >
          <span className="text-sm text-txtHeading font-semibold">
            Scoring Option
          </span>
          <span className="text-xs text-txtBody">
            Select how scores are calculated and which formula to apply.
          </span>
          <div className="flex flex-col gap-2 mt-2">
            <RadioButton
              name={`scoring-option-${ScoringOptionsEnum.RAW}`}
              label="Raw Value"
              labelClassName="text-sm font-medium"
              onCheck={() => setSelectedScoringOption(ScoringOptionsEnum.RAW)}
              checked={selectedScoringOption === ScoringOptionsEnum.RAW}
            />
            <span className="ml-6 text-xs text-txtBody">
              Calculate scores directly from original data.
            </span>
            <RadioButton
              name={`scoring-option-${ScoringOptionsEnum.SCALED}`}
              label="Scaled Value"
              labelClassName="text-sm font-medium"
              onCheck={() =>
                setSelectedScoringOption(ScoringOptionsEnum.SCALED)
              }
              checked={selectedScoringOption === ScoringOptionsEnum.SCALED}
            />
            <span className="ml-6 text-xs text-txtBody">
              Use a log10 transformation to adjust for data variation.
            </span>
          </div>
        </div>
      </div>
      {/* Data Weight Summary Section */}
      <div className="flex flex-col items-center bg-white p-4 pt-2">
        <div className="bg-gray-50 rounded-lg p-3 w-full">
          <div className="flex justify-between mb-2">
            <span className="text-xs text-txtBody">Total Dataset</span>
            <span className="text-txtBody text-xs">
              {Object.keys(availableDatasets).length}
            </span>
          </div>
          <div className="flex justify-between mb-2">
            <span className="text-xs text-txtBody">Total Attributes</span>
            <span className="text-txtBody text-xs">{totalAttributes}</span>
          </div>
          <div className="flex justify-between mb-2 border-t border-gainsboro pt-3 ">
            <span className="text-xs text-txtBody">
              Total Weight Percentage
            </span>
            <span className="text-txtHeading font-semibold">
              {totalPercentage}
            </span>
          </div>
          <Button
            className="
              h-[32px] w-full py-1 mt-3 btn-secondary text-sm font-semibold
              disabled:border-gainsboro disabled:text-gainsboro
            "
            disabled={!hasWeightValuesChanged}
            onClick={resetAllWeights}
          >
            <span>Reset to Default</span>
          </Button>
          {isAllWeightZeroResult && (
            <div className="text-red-600 text-[10px] w-full text-center mt-2">
              {MESSAGE.SETTING_WEIGHT_VALUES_INVALID}
            </div>
          )}
        </div>
        {/* Footer */}
        <Button
          disabled={!selectedScoringOption || isAllWeightZeroResult}
          className={twMerge(
            "w-full mx-4 mt-2 border bg-teal-500 text-white text-sm font-bold",
            "disabled:border-none disabled:bg-gainsboro disabled:text-white"
          )}
          onClick={onSaveSetting}
          data-testid={`${TEST_ID_PREFIX}-save-button`}
        >
          <span>Save</span>
        </Button>
      </div>
    </div>
  );
};

export default DataWeightSettingsPopup;
